import React, {
  useEffect,
  useState,
  useCallback,
  useMemo
} from 'react';
import {
  Tabs,
  Tab,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Stack,
  CircularProgress,
  Alert,
  Container,
  TextField,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  LocalShipping,
  PlayArrow,
  Done,
  Error as ErrorIcon,
  Info,
  Inventory,
  ScannerOutlined,
  Assignment,
  Print,
} from '@mui/icons-material';

// Hooks and Services
import { DataService } from '../../services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { useNavigate, useParams } from 'react-router';

// Components
import Loader from 'src/components/Loader';
import StatusCard from 'src/components/StatusCard';
import ErrorBoundary from 'src/components/ErrorBoundary';
import { BoxProp, BoxItem, BoxStatus, Shipment, ShipmentStatus } from 'src/types/Shipment.types';
import { useJsBarcode } from './shipmentDetailsComponent/BarcodeDisplay';
import BoxDialog from './shipmentDetailsComponent/BoxDialog';
import BoxesTable from './shipmentDetailsComponent/BoxesTable';
import ItemsTable from './shipmentDetailsComponent/ItemsTable';
import ShipmentMovementTab from './shipmentDetailsComponent/ShipmentMovementTab';
import ShipmentHeader from './shipmentDetailsComponent/ShipmentHeader';
import ShipmentInfo from './shipmentDetailsComponent/ShipmentInfoComponent';
import ShipmentProgress from './shipmentDetailsComponent/ShipmentProgress';
import ShipmentGeneralInfoTab from './shipmentDetailsComponent/ShipmentGeneralInfoTab';

// Helper function - moved outside component to prevent recreation
async function readData<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status}: ${txt || res.statusText}`);
  }
  const body = await res.json().catch(() => null);

  if (body && typeof body === "object" && "data" in body) {
    // If success is present and false, treat as error
    if ("success" in body && body.success === false) {
      const msg = typeof body.message === "string" && body.message.trim()
        ? body.message
        : "Request failed";
      throw new Error(msg);
    }
    return body.data as T;
  }
  return body as T;
}

type LabelSize = 'A4' | 'A5';
type PrintTarget =
  | { kind: 'single'; box: BoxProp }
  | { kind: 'all' };

const ShipmentDetails: React.FC = () => {
  useJsBarcode();

  // Main State
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [hasBeenReceived, setHasBeenReceived] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  // Pagination states for boxes and items
  const [boxesPage, setBoxesPage] = useState(1);
  const [boxesPageSize, setBoxesPageSize] = useState(50);
  const [boxesTotalCount, setBoxesTotalCount] = useState(0);
  const [boxesLoading, setBoxesLoading] = useState(false);

  const [itemsPage, setItemsPage] = useState(1);
  const [itemsPageSize, setItemsPageSize] = useState(50);
  const [itemsTotalCount, setItemsTotalCount] = useState(0);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [showDiscrepanciesOnly, setShowDiscrepanciesOnly] = useState(false);

  const [discrepancies, setDiscrepancies] = useState<any[]>([]);

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Box Management
  const [actualBoxCount, setActualBoxCount] = useState<string>('');
  const [selectedBox, setSelectedBox] = useState<BoxProp | null>(null);
  const [boxSearchTerm, setBoxSearchTerm] = useState('');
  const [itemSearchTerm, setItemSearchTerm] = useState('');

  // Table State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [boxDetailsOpen, setBoxDetailsOpen] = useState(false);
  const [arrivalDate, setArrivalDate] = useState<string>('');

  const dispatch = useAppDispatch();
  const { refNumber } = useParams<{ refNumber: string }>();

  // Box status filter
  const [statusFilter, setStatusFilter] = useState<'all' | BoxStatus>('all');

  // Dialogs
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(false);

  const nav = useNavigate();

  // Set activeStep based on shipment status
  useEffect(() => {
    if (!shipment) return;

    switch (shipment.status) {
      case ShipmentStatus.Expected:
        setActiveStep(0);
        break;
      case ShipmentStatus.InReception:
      case ShipmentStatus.Received:
        setActiveStep(1);
        break;
      case ShipmentStatus.Analyzing:
        setActiveStep(2);
        break;
      case ShipmentStatus.Analyzed:
      case ShipmentStatus.Completing:
        setActiveStep(3);
        break;
      case ShipmentStatus.Completed:
        setActiveStep(4); // All steps completed
        break;
      default:
        setActiveStep(0);
    }
  }, [shipment?.status]);

  const handleFileDownload = async (fileUrl: string, fileName?: string) => {
    setDownloadingFile(true);
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      dispatch(
        showAlert({
          message: `File downloaded successfully: ${fileName || 'download'}`,
          type: 'success'
        })
      );
    } catch (error: any) {
      console.error('Download error:', error);
      dispatch(
        showAlert({
          message: `Failed to download file: ${error.message}`,
          type: 'error'
        })
      );
    } finally {
      setDownloadingFile(false);
    }
  };

  // Load boxes data with pagination
  const loadBoxes = useCallback(async (shipmentId: string, pageNum: number = 1, pageSize: number = 50, searchTerm: string = '') => {
    setBoxesLoading(true);
    try {
      let url = `api/shipment/${shipmentId}/boxes?pageNumber=${pageNum}&pageSize=${pageSize}&includeUnitDetails=true`;
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const response = await DataService.get(url);
      if (!response.ok) {
        throw new Error('Failed to load boxes');
      }

      const pagedResult = await readData<any>(response);

      const mappedBoxes = (pagedResult.items || []).map((box: any) => ({
        id: box.id,
        refNumber: box.refNumber,
        barcode: box.barcode,
        status:
          typeof box.status === 'number'
            ? box.status
            : BoxStatus[box.status as keyof typeof BoxStatus] || BoxStatus.New,
        boxItems: (box.boxItems || []).map((item: any) => ({
          id: item.id,
          barcode: item.barcode,
          qty: item.qty,
          unitDetails: item.unitDetails
        }))
      }));

      setShipment((prev) => prev ? { ...prev, boxes: mappedBoxes } : null);
      setBoxesTotalCount(pagedResult.totalCount || 0);
    } catch (err: any) {
      dispatch(
        showAlert({
          message: err.message || 'Failed to load boxes',
          type: 'error'
        })
      );
    } finally {
      setBoxesLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (shipment?.id && boxesPage > 0) {
      loadBoxes(shipment.id, boxesPage, boxesPageSize, boxSearchTerm);
    }
  }, [shipment?.id, boxesPage, boxesPageSize, boxSearchTerm, loadBoxes]);

  // Keyboard shortcuts for auto-focus
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b' && tabValue === 0) {
        e.preventDefault();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'i' && tabValue === 1) {
        e.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [tabValue]);

  const handleMainTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Auto-refresh when shipment is in Completing status
  useEffect(() => {
    if (shipment?.status !== ShipmentStatus.Completing) {
      setIsPolling(false);
      return;
    }

    setIsPolling(true);
    const intervalId = setInterval(async () => {
      // Prevent overlapping requests
      if (processing) return;

      try {
        const response = await DataService.get(`api/shipment/ref/${refNumber}`);
        if (response.ok) {
          const data = await readData<any>(response);
          const newStatus = typeof data.status === 'number'
            ? data.status
            : ShipmentStatus[data.status as keyof typeof ShipmentStatus];

          // Update shipment if status changed
          if (newStatus !== ShipmentStatus.Completing) {
            const mappedShipment: Shipment = {
              id: data.id,
              refNumber: data.refNumber,
              supplierId: data.supplierId,
              supplierName: data.supplierName,
              expectedDate: data.expectedDate,
              actualArrivalDate: data.actualArrivalDate,
              expectedBoxCount: data.expectedBoxCount,
              receivedBoxCount: data.receivedBoxCount,
              status: newStatus,
              statusName: data.statusName,
              implementationFile: data.implementationFile,
              report: data.report,
              notes: data.notes,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
              isOverdue: data.isOverdue,
              hasDiscrepancy: data.hasDiscrepancy,
              boxDiscrepancy: data.boxDiscrepancy,
              delayDuration: data.delayDuration,
              supplier: data.supplier,
              discrepancySummary: data.discrepancySummary || null,
              stats: data.stats,
              boxes: shipment?.boxes || [],
              totalExpectedItems: data.totalExpectedItems,
              totalReceivedItems: data.totalReceivedItems,
              positiveDiscrepancyCount: data.positiveDiscrepancyCount,
              negativeDiscrepancyCount: data.negativeDiscrepancyCount,
            };
            setShipment(mappedShipment);
            setIsPolling(false);

            if (newStatus === ShipmentStatus.Completed) {
              dispatch(
                showAlert({
                  message: 'Shipment completed successfully!',
                  type: 'success'
                })
              );
            }
          }
        }
      } catch (err) {
        console.error('Error refreshing shipment status:', err);
      }
    }, 30000); // Refresh every 30 seconds

    return () => {
      clearInterval(intervalId);
      setIsPolling(false);
    };
  }, [shipment?.status, refNumber, dispatch, processing]);


  // Load shipment items with pagination
  const loadItems = useCallback(async (shipmentId: string, pageNum: number = 1, pageSize: number = 50, searchTerm: string = '', onlyDiscrepancies: boolean = false) => {
    setItemsLoading(true);
    try {
      let url = `api/shipment/${shipmentId}/items?pageNumber=${pageNum}&pageSize=${pageSize}&includeUnitDetails=true`;
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      if (onlyDiscrepancies) {
        url += `&onlyDiscrepancies=true`;
      }

      const response = await DataService.get(url);
      if (!response.ok) {
        throw new Error('Failed to load items');
      }

      const pagedResult = await readData<any>(response);
      setItemsTotalCount(pagedResult.totalCount || 0);

      return pagedResult.items || [];
    } catch (err: any) {
      dispatch(
        showAlert({
          message: err.message || 'Failed to load items',
          type: 'error'
        })
      );
      return [];
    } finally {
      setItemsLoading(false);
    }
  }, [dispatch]);

  // Load discrepancies (non-paginated)
  const loadDiscrepancies = useCallback(async (shipmentId: string) => {
    try {
      const response = await DataService.get(`api/shipment/${shipmentId}/discrepancies`);
      if (!response.ok) {
        throw new Error('Failed to load discrepancies');
      }

      const data = await readData<any>(response);
      setDiscrepancies(data || []);
    } catch (err: any) {
      console.error('Failed to load discrepancies:', err);
    }
  }, []);

  // Load shipment data (lightweight - no nested data)
  useEffect(() => {
    const loadShipment = async () => {
      setLoading(true);
      try {
        if (!refNumber) {
          dispatch(
            showAlert({
              message: 'No shipment reference found in url',
              type: 'error'
            })
          );
          setLoading(false);
          return;
        }

        // Load lightweight shipment details (no boxes/items)
        const response = await DataService.get(`api/shipment/ref/${refNumber}`);
        if (!response.ok) {
          setError('Shipment not found');
          setLoading(false);
          return;
        }

        const data = await readData<any>(response);

        const mappedShipment: Shipment = {
          id: data.id,
          refNumber: data.refNumber,
          supplierId: data.supplierId,
          supplierName: data.supplierName,
          expectedDate: data.expectedDate,
          actualArrivalDate: data.actualArrivalDate,
          expectedBoxCount: data.expectedBoxCount,
          receivedBoxCount: data.receivedBoxCount,
          status:
            typeof data.status === 'number'
              ? data.status
              : ShipmentStatus[data.status as keyof typeof ShipmentStatus],
          statusName: data.statusName,
          implementationFile: data.implementationFile,
          report: data.report,
          notes: data.notes,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          isOverdue: data.isOverdue,
          hasDiscrepancy: data.hasDiscrepancy,
          boxDiscrepancy: data.boxDiscrepancy,
          delayDuration: data.delayDuration,
          supplier: data.supplier,
          discrepancySummary: data.discrepancySummary || null,
          stats: data.stats,
          boxes: [],
          totalExpectedItems: data.totalExpectedItems,
          totalReceivedItems: data.totalReceivedItems,
          positiveDiscrepancyCount: data.positiveDiscrepancyCount,
          negativeDiscrepancyCount: data.negativeDiscrepancyCount
        };

        setShipment(mappedShipment);
        setHasBeenReceived(mappedShipment.status >= ShipmentStatus.InReception);

        if (mappedShipment.id) {
          const promises = [loadBoxes(mappedShipment.id, 1, boxesPageSize)];

          if (mappedShipment.hasDiscrepancy) {
            promises.push(loadDiscrepancies(mappedShipment.id));
          }

          await Promise.all(promises);
        }
      } catch (err: any) {
        dispatch(
          showAlert({
            message: err.message || 'Failed to load shipment data',
            type: 'error'
          })
        );
        setError(err.message || 'Failed to load shipment data');
      } finally {
        setLoading(false);
      }
    };
    loadShipment();
  }, [refNumber]);

  const handleAcceptShipment = async () => {
    const actualCount = parseInt(actualBoxCount);
    if (!actualCount || actualCount <= 0) {
      alert('Please enter a valid number of boxes received');
      return;
    }

    setProcessing(true);
    try {
      let formattedDate: string;
      if (arrivalDate) {
        formattedDate = arrivalDate.includes('T')
          ? arrivalDate
          : `${arrivalDate}T00:00:00`;
      } else {
        formattedDate = new Date().toISOString();
      }

      const payload = {
        id: shipment!.id,
        receivedBoxCount: actualCount,
        arrivalDate: formattedDate
      };

      const response = await DataService.post(
        `api/Shipment/${shipment.id}/receive`,
        payload
      );
      if (!response.ok) {
        dispatch(
          showAlert({ message: 'Failed to receive shipment!', type: 'error' })
        );
        setProcessing(false);
        return;
      }

      const refreshed = await DataService.get(
        `api/Shipment/ref/${shipment!.refNumber}`
      );
      if (refreshed.ok) {
        const data = await readData<any>(refreshed);
        const mappedShipment: Shipment = {
          id: data.id,
          refNumber: data.refNumber,
          supplierId: data.supplierId,
          supplierName: data.supplierName,
          expectedDate: data.expectedDate,
          actualArrivalDate: data.actualArrivalDate,
          expectedBoxCount: data.expectedBoxCount,
          receivedBoxCount: data.receivedBoxCount,
          status:
            typeof data.status === 'number'
              ? data.status
              : ShipmentStatus[data.status as keyof typeof ShipmentStatus],
          statusName: data.statusName,
          implementationFile: data.implementationFile,
          report: data.report,
          notes: data.notes,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          isOverdue: data.isOverdue,
          hasDiscrepancy: data.hasDiscrepancy,
          boxDiscrepancy: data.boxDiscrepancy,
          delayDuration: data.delayDuration,
          supplier: data.supplier,
          discrepancySummary: data.discrepancySummary || null,
          stats: data.stats,
          boxes: [],
          totalExpectedItems: data.totalExpectedItems,
          totalReceivedItems: data.totalReceivedItems,
          positiveDiscrepancyCount: data.positiveDiscrepancyCount,
          negativeDiscrepancyCount: data.negativeDiscrepancyCount
        };
        setShipment(mappedShipment);

        // Reload boxes with pagination
        if (mappedShipment.id) {
          await loadBoxes(mappedShipment.id, 1, boxesPageSize);
        }
      }
      setHasBeenReceived(true);
      setAcceptDialogOpen(false);
      setActiveStep(1);
    } catch (err) {
      dispatch(
        showAlert({ message: 'Failed to accept shipment', type: 'error' })
      );
    } finally {
      setProcessing(false);
    }
  };

  /*** BARCODE GENERATION — STICKER VERSION (no text) ***/
  const generateBarcodeForPrint = useCallback((value: string, opts?: any): string => {
    const canvas = document.createElement('canvas');
    // High-ish resolution for crisp printing on a small label
    canvas.width = 600;
    canvas.height = 300;

    const JsBarcode = (window as any).JsBarcode;
    if (JsBarcode) {
      try {
        JsBarcode(canvas, value, {
          format: 'CODE128',
          width: 2,            // bar thickness
          height: 220,         // fit inside 25mm height at this scale
          displayValue: false, // HIDE human readable text
          margin: 0,           // no margin
          background: '#ffffff',
          lineColor: '#000000',
          ...opts,
        });
        return canvas.toDataURL('image/png');
      } catch (error) {
        console.warn('JsBarcode generation failed:', error);
      }
    }
    return `data:image/svg+xml;base64,${btoa('<svg>...</svg>')}`;
  }, []);

  /*** 50mm x 25mm PAGE STYLES ***/
  const getStickerStyles = useCallback(() => {
    return `
      @page {
        size: 50mm 25mm; /* 5 x 2.5 cm */
        margin: 0;
      }
      html, body {
        margin: 0;
        padding: 0;
        height: 100%;
        width: 100%;
        background: #ffffff;
      }
      .page {
        width: 50mm;
        height: 25mm;
        display: flex;
        align-items: center;
        justify-content: center;
        page-break-after: always;
        background: #ffffff;
      }
      .barcode-wrap {
        width: 46mm; /* small inner margin */
        height: 21mm;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .barcode-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
        image-rendering: -webkit-optimize-contrast;
        image-rendering: crisp-edges;
      }
    `;
  }, []);

  /*** MINIMAL HTML FOR A SINGLE STICKER ***/
  const buildStickerHtml = useCallback((box: BoxProp) => {
    const barcodeImage = generateBarcodeForPrint(box.barcode, { displayValue: false });
    return `
      <div class="page">
        <div class="barcode-wrap">
          <img src="${barcodeImage}" alt="Barcode" class="barcode-image" />
        </div>
      </div>
    `;
  }, [generateBarcodeForPrint]);

  /*** PRINT A SINGLE OR ALL STICKERS ***/
  const printBarcodeSticker = useCallback((target: PrintTarget) => {
    if (!shipment) return;
    const styles = getStickerStyles();

    let pagesHtml = '';
    if (target.kind === 'single') {
      pagesHtml = buildStickerHtml(target.box);
    } else {
      pagesHtml = shipment.boxes.map((b) => buildStickerHtml(b)).join('');
    }

    const doc = `
      <html>
        <head>
          <title>Sticker${target.kind === 'all' ? 's' : ''} - ${shipment.refNumber}</title>
          <style>${styles}</style>
        </head>
        <body>${pagesHtml}</body>
      </html>
    `;

    const w = window.open('', '_blank');
    if (w) {
      w.document.write(doc);
      w.document.close();
      w.focus();
      w.print();
    }
  }, [shipment, getStickerStyles, buildStickerHtml]);

  // --- handlers now point directly to sticker print (no A4/A5 dialog) ---
  const onPrintBoxLabelRequest = useCallback((box: BoxProp) => {
    printBarcodeSticker({ kind: 'single', box });
  }, [printBarcodeSticker]);

  const onPrintAllBoxLabelsRequest = useCallback(() => {
    printBarcodeSticker({ kind: 'all' });
  }, [printBarcodeSticker]);

  const addItemToBox = useCallback((boxId: string, barcode: string) => {
    setShipment((prev) => {
      if (!prev) return prev;

      const updatedBoxes = prev.boxes.map((box) => {
        if (box.id !== boxId) return box;

        const existingItem = box.boxItems.find(
          (item) => item.barcode === barcode
        );
        let updatedItems;

        if (existingItem) {
          updatedItems = box.boxItems.map((item) =>
            item.barcode === barcode ? { ...item, qty: item.qty + 1 } : item
          );
        } else {
          const newItem: BoxItem = {
            id: `item-${Date.now()}-${Math.random()}`,
            barcode,
            qty: 1
          };
          updatedItems = [...box.boxItems, newItem];
        }

        return { ...box, boxItems: updatedItems };
      });

      const updated = { ...prev, boxes: updatedBoxes };
      if (selectedBox?.id === boxId) {
        const latestBox = updatedBoxes.find((box) => box.id === boxId);
        if (latestBox) {
          setSelectedBox(latestBox);
        }
      }

      return updated;
    });
  }, [selectedBox?.id]);

  const updateItemQuantity = useCallback((
    boxId: string,
    itemId: string,
    change: number
  ) => {
    setShipment((prev) => {
      if (!prev) return prev;

      const updatedBoxes = prev.boxes.map((box) => {
        if (box.id !== boxId) return box;

        const updatedItems = box.boxItems
          .map((item) => {
            if (item.id !== itemId) return item;
            const newQty = Math.max(0, item.qty + change);
            return { ...item, qty: newQty };
          })
          .filter((item) => item.qty > 0);

        return { ...box, boxItems: updatedItems };
      });

      const updated = { ...prev, boxes: updatedBoxes };
      if (selectedBox?.id === boxId) {
        const latestBox = updatedBoxes.find((box) => box.id === boxId);
        if (latestBox) {
          setSelectedBox(latestBox);
        }
      }

      return updated;
    });
  }, [selectedBox?.id]);

  const updateBoxItems = useCallback(async (box: BoxProp) => {
    try {
      const payload = {
        id: box.id,
        boxStatus: box.status,
        boxItems: box.boxItems.map((item) => ({
          Id: item.id,
          Barcode: item.barcode,
          Qty: item.qty
        }))
      };

      const response = await DataService.post('api/Box/update-items', payload);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        dispatch(
          showAlert({
            message: `Failed to update box items: ${
              errorData.message || 'Unknown error'
            }`,
            type: 'error'
          })
        );
        return;
      }

      dispatch(
        showAlert({
          message: `Box ${box.refNumber} updated successfully`,
          type: 'success'
        })
      );
    } catch (err: any) {
      console.error('Error updating box items:', err);
      dispatch(
        showAlert({
          message: `Failed to update box items: ${
            err.message || 'Network error'
          }`,
          type: 'error'
        })
      );
    }
  }, [dispatch]);

  const [allItems, setAllItems] = useState<any[]>([]);

  const updateBoxStatus = useCallback(async (boxId: string, newStatus: BoxStatus) => {
    const box = shipment?.boxes.find((b) => b.id === boxId);
    if (!box) return;

    // Show loading state
    setProcessing(true);

    try {
      const updatedBox = { ...box, status: newStatus };

      const payload = {
        id: updatedBox.id,
        boxStatus: newStatus,
        boxItems: updatedBox.boxItems.map((item) => ({
          Id: item.id,
          Barcode: item.barcode,
          Qty: item.qty
        }))
      };

      const response = await DataService.post('api/Box/update-items', payload);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        dispatch(
          showAlert({
            message: `Failed to update box status: ${
              errorData.message || 'Unknown error'
            }`,
            type: 'error'
          })
        );
        return;
      }

      // Update local state only after successful API call
      setShipment((prev) => {
        if (!prev) return prev;
        const updatedBoxes = prev.boxes.map((box) =>
          box.id === boxId ? { ...box, status: newStatus } : box
        );
        return { ...prev, boxes: updatedBoxes };
      });

      // Update selected box if it's the one being updated
      if (selectedBox?.id === boxId) {
        setSelectedBox((prev) =>
          prev ? { ...prev, status: newStatus } : null
        );
      }

      dispatch(
        showAlert({
          message: `Box ${box.refNumber} status updated to ${BoxStatus[newStatus]}`,
          type: 'success'
        })
      );
    } catch (err: any) {
      console.error('Error updating box status:', err);
      dispatch(
        showAlert({
          message: `Failed to update box status: ${
            err.message || 'Network error'
          }`,
          type: 'error'
        })
      );
    } finally {
      setProcessing(false);
    }
  }, [shipment?.boxes, selectedBox?.id, dispatch]);

  const printItemsList = useCallback((box?: any) => {
    const items = box ? box.boxItems : allItems;
    const title = box ? `Items in Box ${box.refNumber}` : `All Items in Shipment ${shipment?.refNumber}`;

    const printContent = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #000; padding: 8px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; }
            .barcode { font-family: monospace; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>${title}</h2>
            <p>Generated on: ${new Date().toLocaleString()}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Item Barcode</th>
                <th>Quantity</th>
                ${!box ? '<th>Box Reference</th>' : ''}
              </tr>
            </thead>
            <tbody>
              ${items.map((item: any, index: number) => `
                <tr>
                  <td>${index + 1}</td>
                  <td class="barcode">${item.barcode}</td>
                  <td>${item.qty}</td>
                  ${!box ? `<td>${(item as any).boxRefNumber || 'N/A'}</td>` : ''}
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div style="margin-top: 20px;">
            <p><strong>Total Items:</strong> ${items.length}</p>
            <p><strong>Total Quantity:</strong> ${items.reduce((sum: number, item: any) => sum + item.qty, 0)}</p>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
  }, [allItems, shipment?.refNumber]);

  const exportToCSV = useCallback(() => {
    if (!shipment) return;
    const data = shipment.boxes.map((box) => ({
      reference: box.refNumber,
      barcode: box.barcode,
      status: BoxStatus[box.status],
      itemCount: box.boxItems.length,
      totalQuantity: box.boxItems.reduce((sum, item) => sum + item.qty, 0)
    }));

    const headers = Object.keys(data[0] || {});
    const csvContent = [
      headers.join(','),
      ...data.map((row) => headers.map((header) => `"${(row as any)[header] || ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `shipment-${shipment.refNumber}-boxes.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  }, [shipment]);

  const handleAnalyzeShipment = async () => {
    if (!shipment) return;

    setProcessing(true);

    try {
      const response = await DataService.post(`api/Shipment/${shipment.id}/analyze`, {});

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        dispatch(
          showAlert({
            message: `Failed to analyze shipment: ${
              errorData.message || 'Unknown error'
            }`,
            type: 'error'
          })
        );
        setProcessing(false);
        return;
      }

      const result = await readData<any>(response);

      dispatch(
        showAlert({
          message: result.message || 'Shipment analyzed successfully! Use "Generate Report" to download the Excel report.',
          type: 'success'
        })
      );

      // Reload the page to ensure latest data is displayed
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (err: any) {
      console.error('Analysis error:', err);
      dispatch(
        showAlert({
          message: `Failed to analyze shipment: ${
            err.message || 'Network error'
          }`,
          type: 'error'
        })
      );
      setProcessing(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!shipment) return;

    setDownloadingFile(true);

    try {
      const response = await DataService.post(`api/Shipment/${shipment.id}/generate-report`, {});

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        dispatch(
          showAlert({
            message: `Failed to generate report: ${
              errorData.message || 'Unknown error'
            }`,
            type: 'error'
          })
        );
        return;
      }

      const result = await readData<any>(response);

      if (result.reportUrl) {
        // Download the report
        await handleFileDownload(result.reportUrl, `${shipment.refNumber}-analysis-report.xlsx`);

        dispatch(
          showAlert({
            message: 'Report generated and downloaded successfully!',
            type: 'success'
          })
        );
      } else {
        dispatch(
          showAlert({
            message: result.message || 'Report generated successfully!',
            type: 'success'
          })
        );
      }

    } catch (err: any) {
      console.error('Report generation error:', err);
      dispatch(
        showAlert({
          message: `Failed to generate report: ${
            err.message || 'Network error'
          }`,
          type: 'error'
        })
      );
    } finally {
      setDownloadingFile(false);
    }
  };

  const handleCompleteShipment = async () => {
    if (!shipment || processing || isPolling) return; // Prevent concurrent operations

    setProcessing(true);

    try {
      // Show completing status immediately
      setShipment((prev) => ({
        ...prev!,
        status: ShipmentStatus.Completing
      }));

      dispatch(
        showAlert({
          message: 'Shipment is being completed... Please wait.',
          type: 'info'
        })
      );

      const response = await DataService.post(`api/Shipment/${shipment.id}/complete`, {});

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        dispatch(
          showAlert({
            message: `Failed to complete shipment: ${
              errorData.message || 'Unknown error'
            }`,
            type: 'error'
          })
        );
        // Revert status on error
        setShipment((prev) => ({
          ...prev!,
          status: ShipmentStatus.Analyzed
        }));
        return;
      }

      dispatch(
        showAlert({
          message: 'Shipment completion initiated.',
          type: 'success'
        })
      );

      // The status will be updated by the polling mechanism

    } catch (err: any) {
      console.error('Complete error:', err);
      dispatch(
        showAlert({
          message: `Failed to complete shipment: ${
            err.message || 'Network error'
          }`,
          type: 'error'
        })
      );
      // Revert status on error
      setShipment((prev) => ({
        ...prev!,
        status: ShipmentStatus.Analyzed
      }));
    } finally {
      setProcessing(false);
    }
  };


  const statusConfig = useMemo(() => {
    if (!shipment) return null;
    
    const statusConfigs = {
      [ShipmentStatus.Processing]: {
        color: 'info' as const,
        icon: <Schedule />,
        label: 'Processing'
      },
      [ShipmentStatus.Expected]: {
        color: 'warning' as const,
        icon: <Schedule />,
        label: 'Expected'
      },
      [ShipmentStatus.InReception]: {
        color: 'info' as const,
        icon: <LocalShipping />,
        label: 'In Reception'
      },
      [ShipmentStatus.Received]: {
        color: 'success' as const,
        icon: <CheckCircle />,
        label: 'Received'
      },
      [ShipmentStatus.Analyzing]: {
        color: 'info' as const,
        icon: <Assignment />,
        label: 'Analyzing'
      },
      [ShipmentStatus.Analyzed]: {
        color: 'success' as const,
        icon: <Done />,
        label: 'Analyzed'
      },
      [ShipmentStatus.Completing]: {
        color: 'info' as const,
        icon: <Assignment />,
        label: 'Completing'
      },
      [ShipmentStatus.Completed]: {
        color: 'success' as const,
        icon: <CheckCircle />,
        label: 'Completed'
      },
      [ShipmentStatus.Allocated]: {
        color: 'info' as const,
        icon: <Inventory />,
        label: 'Allocated'
      },
      [ShipmentStatus.Cancelled]: {
        color: 'error' as const,
        icon: <ErrorIcon />,
        label: 'Cancelled'
      }
    };

    return statusConfigs[shipment.status] || {
      color: 'default' as const,
      icon: <Info />,
      label: String(shipment.status)
    };
  }, [shipment?.status]);

  const boxStats = useMemo(() => {
    if (!shipment?.boxes || shipment.boxes.length === 0) {
      return {
        totalScannedItems: 0,
        completedBoxes: 0,
        pausedBoxes: 0,
        newBoxes: 0
      };
    }

    return shipment.boxes.reduce(
      (acc, box) => {
        acc.totalScannedItems += box.boxItems.length;
        if (box.status === BoxStatus.Completed) acc.completedBoxes++;
        else if (box.status === BoxStatus.Paused) acc.pausedBoxes++;
        else if (box.status === BoxStatus.New) acc.newBoxes++;
        return acc;
      },
      { totalScannedItems: 0, completedBoxes: 0, pausedBoxes: 0, newBoxes: 0 }
    );
  }, [shipment?.boxes]);

  const { totalScannedItems, completedBoxes, pausedBoxes, newBoxes } = boxStats;
  const hasAnyDiscrepancy = Boolean(
      shipment?.discrepancySummary?.hasDiscrepancy ?? shipment?.hasDiscrepancy
  );

  useEffect(() => {
    if (tabValue === 1 && shipment?.id) {
      const fetchItems = async () => {
        // Clear old items immediately to prevent showing stale data
        setAllItems([]);
        const items = await loadItems(shipment.id, itemsPage, itemsPageSize, itemSearchTerm, showDiscrepanciesOnly);
        setAllItems(items);
      };
      fetchItems();
    }
  }, [tabValue, shipment?.id, itemsPage, itemsPageSize, itemSearchTerm, showDiscrepanciesOnly, loadItems]);

  if (loading) {
    return <Loader />;
  }

  if (error || !shipment) {
    return (
      <StatusCard
        layout="page"
        variant="not-found"
        imageSrc={process.env.PUBLIC_URL + '/static/images/status/error1.svg'}
        title="Shipment Not Found"
        message="Sorry, this shipment doesn't exist. Check the reference or go back."
        actionLabel="Back to List"
        onAction={() => nav('/shipments')}
      />
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 3 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <ShipmentHeader
          shipment={shipment}
          statusConfig={statusConfig}
          isSaving={false}
          processing={processing}
          downloadingFile={downloadingFile}
          onAcceptShipment={() => setAcceptDialogOpen(true)}
          onAnalyzeShipment={handleAnalyzeShipment}
          onCompleteShipment={handleCompleteShipment}
          onGenerateReport={handleGenerateReport}
          totalScannedItems={totalScannedItems}
        />

        {/* Discrepancy Alert */}
        {hasAnyDiscrepancy && shipment.status !== ShipmentStatus.Completed && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => handleFileDownload(shipment.report!, `${shipment.refNumber}-discrepancy-report.xlsx`)}
                startIcon={downloadingFile ? <CircularProgress size={16} /> : <Assignment />}
                disabled={downloadingFile || !shipment.report}
                sx={{ textTransform: 'none' }}
              >
                {downloadingFile ? 'Downloading...' : 'Download Report'}
              </Button>
            }
          >
            <Typography variant="body1" fontWeight="600">
              Discrepancies Detected
            </Typography>
            <Typography variant="body2">
              This shipment has discrepancies that need to be reviewed. Download the report to see details.
            </Typography>
          </Alert>
        )}

        {/* Status-specific Alerts */}
        {shipment.status === ShipmentStatus.Processing && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="600">
              Processing Shipment
            </Typography>
            <Typography variant="body2">
              This shipment is currently being processed. Receiving will be available once processing is complete.
            </Typography>
          </Alert>
        )}
        
        {shipment.status === ShipmentStatus.Analyzing && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="600">
              Analyzing Shipment
            </Typography>
            <Typography variant="body2">
              This shipment is currently being analyzed. Please wait for the analysis to complete.
            </Typography>
          </Alert>
        )}

        {shipment.status === ShipmentStatus.Analyzed && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="600">
              Shipment Analyzed
            </Typography>
            <Typography variant="body2">
              Analysis complete. Review the discrepancies report and mark items for completion.
            </Typography>
          </Alert>
        )}

        {shipment.status === ShipmentStatus.Completing && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="600">
              Completing Shipment
            </Typography>
            <Typography variant="body2">
              This shipment is being marked as complete. All items are being finalized.
            </Typography>
          </Alert>
        )}

        {shipment.status === ShipmentStatus.Completed && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="600">
              Shipment Completed
            </Typography>
            <Typography variant="body2">
              This shipment has been successfully completed. All items have been processed.
            </Typography>
          </Alert>
        )}

        {shipment.status === ShipmentStatus.Allocated && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="600">
              Shipment Allocated
            </Typography>
            <Typography variant="body2">
              This shipment has been successfully allocated to storage locations. All items have been transferred.
            </Typography>
          </Alert>
        )}
        
        {shipment.status === ShipmentStatus.Cancelled && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="600">
              Shipment Cancelled
            </Typography>
            <Typography variant="body2">
              This shipment has been cancelled. No further actions are available.
            </Typography>
          </Alert>
        )}

        {/* Progress Card */}
        {shipment.status === ShipmentStatus.InReception && (
          <ShipmentProgress
            newBoxes={newBoxes}
            pausedBoxes={pausedBoxes}
            completedBoxes={completedBoxes}
            totalScannedItems={totalScannedItems}
          />
        )}

        {/* Main Tabs Container */}
        <Card sx={{ mb: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleMainTabChange}>
              <Tab
                label="Reception"
                icon={<ScannerOutlined />}
                iconPosition="start"
                disabled={shipment.status === ShipmentStatus.Processing || shipment.status === ShipmentStatus.Cancelled}
              />
              <Tab label="Details" icon={<Assignment />} iconPosition="start" />
              <Tab label="General Information" icon={<Assignment />} iconPosition="start" />
            </Tabs>
          </Box>

          {/* TAB 0: Reception Interface */}
          {tabValue === 0 && (
            <Box sx={{ p: 0 }}>
              {/* Process Timeline */}
              <Card sx={{ m: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: '600', mb: 3 }}
                  >
                    Reception Progress
                  </Typography>
                  <Stepper activeStep={activeStep} alternativeLabel>
                    {[
                      'Accept Shipment',
                      'Scan Items',
                      'Analyze Shipment',
                      'Complete Shipment'
                    ].map((label, index) => {
                      const isCompleted = index < activeStep;
                      const isActive = index === activeStep;
                      return (
                        <Step key={label} completed={isCompleted}>
                          <StepLabel
                            StepIconProps={{
                              sx: {
                                color: isCompleted ? 'success.main' : 
                                       isActive ? 'primary.main' : 
                                       'grey.400',
                                '&.Mui-active': { color: 'primary.main' },
                                '&.Mui-completed': { color: 'success.main' }
                              }
                            }}
                            sx={{
                              '& .MuiStepLabel-label': {
                                color: isCompleted ? 'success.dark' : 
                                       isActive ? 'primary.main' : 
                                       'text.secondary',
                                fontWeight: isActive || isCompleted ? 600 : 400
                              }
                            }}
                          >
                            {label}
                          </StepLabel>
                        </Step>
                      );
                    })}
                  </Stepper>
                </CardContent>
              </Card>

              <Grid container spacing={3} sx={{ px: 3, pb: 3 }}>
                {/* Left Sidebar - Shipment Info */}
                <Grid item xs={12} lg={3}>
                  <Stack spacing={2}>
                    <ShipmentInfo
                      shipment={shipment}
                      selectedBox={selectedBox}
                      onFileDownload={handleFileDownload}
                      downloadingFile={downloadingFile}
                    />
                  </Stack>
                </Grid>

                {/* Main Content - Boxes Table */}
                <Grid item xs={12} lg={9}>
                  {boxesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                      <Stack spacing={2} alignItems="center">
                        <CircularProgress size={48} />
                        <Typography variant="body2" color="text.secondary">
                          Loading boxes...
                        </Typography>
                      </Stack>
                    </Box>
                  ) : (
                    <BoxesTable
                      boxes={shipment.boxes}
                      shipmentStatus={shipment.status}
                      boxSearchTerm={boxSearchTerm}
                      onBoxSearchChange={(term) => {
                        setBoxSearchTerm(term);
                        setBoxesPage(1);
                      }}
                      statusFilter={statusFilter}
                      onStatusFilterChange={setStatusFilter}
                      selectedBox={selectedBox}
                      onBoxSelect={setSelectedBox}
                      onBoxDetailsOpen={(box) => {
                        setSelectedBox(box);
                        setBoxDetailsOpen(true);
                      }}
                      onPrintBoxLabel={onPrintBoxLabelRequest}          // prints 50x25 barcode-only
                      onUpdateBoxStatus={updateBoxStatus}
                      onPrintAllBoxLabels={onPrintAllBoxLabelsRequest}  // prints 50x25 for all
                      onPrintItemsList={() => printItemsList()}
                      onPrintAllItems={() => printItemsList()}
                      onExportToCSV={exportToCSV}
                      autoFocus={true}
                      page={boxesPage - 1}
                      rowsPerPage={boxesPageSize}
                      onPageChange={(newPage) => setBoxesPage(newPage + 1)}
                      onRowsPerPageChange={(newSize) => {
                        setBoxesPageSize(newSize);
                        setBoxesPage(1);
                      }}
                      totalCount={boxesTotalCount}
                    />
                  )}
                </Grid>
              </Grid>
            </Box>
          )}

          {/* TAB 1: Details */}
          {tabValue === 2 && (
            <ShipmentGeneralInfoTab
              shipment={shipment}
              onDownloadFile={handleFileDownload}
              downloadingFile={downloadingFile}
            />
          )}

          {tabValue === 1 && (
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                {itemsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, p: 4 }}>
                    <Stack spacing={2} alignItems="center">
                      <CircularProgress size={48} />
                      <Typography variant="body2" color="text.secondary">
                        Loading items...
                      </Typography>
                    </Stack>
                  </Box>
                ) : (
                  <ItemsTable
                    items={allItems}
                    itemSearchTerm={itemSearchTerm}
                    onItemSearchChange={(term) => {
                      setItemSearchTerm(term);
                      setItemsPage(1);
                    }}
                    autoFocus={true}
                    showAdditionalProperties={true}
                    totalCount={itemsTotalCount}
                    page={itemsPage - 1}
                    rowsPerPage={itemsPageSize}
                    onPageChange={(newPage) => setItemsPage(newPage + 1)}
                    onRowsPerPageChange={(newSize) => {
                      setItemsPageSize(newSize);
                      setItemsPage(1);
                    }}
                    onFilterDiscrepancies={(onlyDiscrepancies) => {
                      setShowDiscrepanciesOnly(onlyDiscrepancies);
                      setItemsPage(1); // Reset to first page when filtering
                    }}
                    showDiscrepanciesOnly={showDiscrepanciesOnly}
                  />
                )}
              </Card>
            </Grid>
          )}

          {/* TAB 3: MOVEMENT HISTORY */}
          {tabValue === 3 && (
            <ShipmentMovementTab
              shipmentRefNumber={shipment.refNumber}
              active={tabValue === 3}
            />
          )}
        </Card>

        {/* Accept Shipment Dialog */}
        <Dialog
          open={acceptDialogOpen}
          onClose={() => !hasBeenReceived && setAcceptDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Accept Shipment</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {hasBeenReceived ? (
                <Alert severity="success">
                  This shipment has already been received successfully.
                </Alert>
              ) : (
                <Typography>
                  Please enter the actual number of boxes received for this
                  shipment.
                </Typography>
              )}
              <TextField
                fullWidth
                label="Actual Boxes Received"
                type="number"
                value={actualBoxCount}
                onChange={(e) => setActualBoxCount(e.target.value)}
                placeholder={`Expected: ${shipment.expectedBoxCount}`}
                helperText={`Expected quantity: ${shipment.expectedBoxCount} boxes`}
                autoFocus={!hasBeenReceived}
                disabled={hasBeenReceived}
              />
              <TextField
                fullWidth
                label="Arrival Date (optional)"
                type="date"
                value={arrivalDate}
                onChange={(e) => setArrivalDate(e.target.value)}
                helperText="Leave blank to use today's date"
                InputLabelProps={{ shrink: true }}
                disabled={hasBeenReceived}
              />
              {!hasBeenReceived && (
                <Alert severity="info">
                  This will generate individual box records that you can scan and
                  manage separately.
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setAcceptDialogOpen(false)}
              disabled={hasBeenReceived && processing}
            >
              {hasBeenReceived ? 'Close' : 'Cancel'}
            </Button>
            {!hasBeenReceived && (
              <Button
                onClick={handleAcceptShipment}
                variant="contained"
                disabled={
                  processing || hasBeenReceived || !actualBoxCount || parseInt(actualBoxCount) <= 0
                }
                startIcon={
                  processing ? <CircularProgress size={20} /> : <PlayArrow />
                }
              >
                Generate {actualBoxCount || 0} Boxes
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Box Details Dialog */}
        <ErrorBoundary
          onReset={() => {
            setBoxDetailsOpen(false);
            setSelectedBox(null);
          }}
        >
          <BoxDialog
            open={boxDetailsOpen}
            onClose={() => setBoxDetailsOpen(false)}
            box={selectedBox}
            shipmentStatus={shipment.status}
            onAddItem={addItemToBox}
            onUpdateItemQuantity={updateItemQuantity}
            onUpdateBoxStatus={updateBoxStatus}
            onPrintBoxLabel={(b) => onPrintBoxLabelRequest(b)}  // prints small sticker from dialog too
            onPrintItemsList={printItemsList}
          />
        </ErrorBoundary>

        {/* (Removed the old A4/A5 print size picker dialog) */}
      </Container>
    </Box>
  );
};

export default ShipmentDetails;
